import React, { useCallback, useId, useState } from "react";
import PropTypes from "prop-types";
import { useDismissable } from "../hooks/useDismissable";

/**
 * The (i) beside a label: a short explanation that opens on hover, on focus,
 * and on tap — a phone has no hover, so the tap is the only way it opens there.
 *
 * The text is wired to the button with aria-describedby whether or not it is
 * showing, so a screen reader hears it without having to open anything.
 */
const InfoTip = ({ text, label }) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismissable(open, close);
  const tipId = useId();

  return (
    <span ref={ref} className="relative inline-flex align-middle">
      {/* 44px to press, 24px to look at: the negative margin keeps the label
          row from growing around the larger hit area. */}
      <button
        type="button"
        aria-label={label}
        aria-describedby={tipId}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={close}
        onFocus={() => setOpen(true)}
        onBlur={close}
        className="-m-2.5 flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-ink-400 transition hover:text-ink-700 focus-visible:outline-2 focus-visible:outline-offset-[-6px] focus-visible:outline-brand-600"
      >
        <i className="bi bi-info-circle text-base" aria-hidden="true" />
      </button>
      <span
        id={tipId}
        role="tooltip"
        className={`${
          open ? "block" : "hidden"
        } absolute top-full left-0 z-30 mt-2 w-60 max-w-[calc(100vw-2rem)] rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs leading-relaxed font-normal text-ink-700 shadow-card`}
      >
        {text}
      </span>
    </span>
  );
};

InfoTip.propTypes = {
  /** What the tip says. */
  text: PropTypes.string.isRequired,
  /** The button's accessible name, e.g. "More about the fiscal code". */
  label: PropTypes.string.isRequired,
};

export default InfoTip;
